import * as icons from '@mui/icons-material';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TooltipIconButtonLink } from 'components/common/icon/tooltip-icon-button';
import moment from 'moment-timezone';
import { FC, ReactNode, useEffect, useState } from 'react';
import {
  Conversation_externalLead_conversation_inboundCall,
  Conversation_externalLead_conversation_inboundSms,
  Conversation_externalLead_conversation_inboundSms_media,
  Conversation_externalLead_conversation_leadEvent,
  Conversation_externalLead_conversation_leadEvent_events_linkClickedDetails,
  Conversation_externalLead_conversation_outboundCall,
  Conversation_externalLead_conversation_recording,
  Conversation_externalLead_scheduledOutreach,
} from 'types/apollo/Conversation';
import { formatDuration } from 'utils/duration';
import AktifyChatIcon from './aktify-chat-icon.svg';
import { extractExpiration, labelForItem } from './conversation-helpers';
import { LeadStatusChip } from './lead-status-chip';

// ============================================================================
// Styled Components
// ============================================================================

export const ChatMessage = styled(Stack)`
  display: flex;
  min-width: 60%;
  max-width: 80%;

  &.left {
    align-self: flex-start;
    align-items: flex-start;
  }

  &.right {
    align-self: flex-end;
    align-items: flex-end;
  }
`;

export const ChatBubble = styled('div')`
  background-color: #318adc;
  color: #fff;
  border-radius: 5px;
  padding: 10px;
  align-self: flex-start;
  margin-left: 8px;
  margin-right: 8px;

  &.right {
    background-color: white;
    color: #2e2e2e;
    align-self: flex-end;
  }

  &.call {
    background-color: #2e2e2e1a;
    color: #2e2e2e;
  }
`;

export const Timestamp = styled('span')`
  font-size: 0.8rem;
  color: #888;
  margin-top: 5px;
  text-align: right;
`;

const CallStatusIcon = styled(Box)(() => ({
  borderRadius: '50%',
  height: 30,
  width: 30,
  backgroundColor: '#2E2E2E',
  color: 'white',
  textAlign: 'center',
  fontSize: 15,
  padding: '2px 0px',
  '&.error': {
    backgroundColor: '#cb6363',
  },
}));

export const AktifyIcon2 = styled(Box)(() => ({
  height: 30,
  content: `url(${AktifyChatIcon})`,
}));

export const LeadChatCircle = styled(Box)(() => ({
  backgroundColor: 'white',
  borderRadius: '50%',
  width: 30,
  height: 30,
  color: '#2E2E2E',
  padding: 4,
  fontWeight: 'bold',
  fontSize: 15,
  textAlign: 'center',
}));

// ============================================================================
// Helper Functions
// ============================================================================

export const iconForItem = (it: Conversation_externalLead_scheduledOutreach): ReactNode => {
  switch (it.kind) {
    case 'REM':
      return <icons.Alarm />;
    case 'SCHEDULE':
      return <icons.Call />;
    case 'FLOW': {
      const intent = it.intent ?? (it.messagePreview ? 'sms' : undefined);
      if (intent === 'call') return <icons.Call />;
      if (intent === 'sms') return <icons.Sms />;
      return <icons.AutoAwesomeMotion />;
    }
    case 'REPLY':
      return <icons.Reply />;
    default:
      return <icons.Schedule />;
  }
};

// ============================================================================
// Components
// ============================================================================

export const LoadingBarOrEmptySpace: FC<{ loading: boolean }> = ({ loading }) => {
  return loading ? (
    <LinearProgress
      sx={{
        height: '5px',
        width: '100%',
      }}
    />
  ) : (
    <div style={{ height: '5px' }} />
  );
};

export const OutboundCallStatus: FC<{
  callEvent: Conversation_externalLead_conversation_outboundCall;
}> = ({ callEvent }) => {
  let icon = <icons.Clear />;
  let className = '';
  let text = '';

  if (callEvent.isDnc) {
    className = 'error';
    text = 'DNC (pressed 9)';
  } else if (callEvent.isTransfer) {
    icon = <icons.NorthWest />;
    text = 'transferred (pressed 1)';
  } else if (callEvent.isReschedule) {
    icon = <icons.NorthWest />;
    text = 'rescheduled (pressed 2)';
  } else {
    className = 'error';
    text = 'no response';
  }

  return (
    <>
      <ChatBubble className="call">
        Outbound call <>&mdash;</> {text}
      </ChatBubble>
      <CallStatusIcon className={className}>{icon}</CallStatusIcon>
    </>
  );
};

export const InboundCallStatus: FC<{
  callEvent: Conversation_externalLead_conversation_inboundCall;
}> = ({ callEvent }) => {
  let icon = <icons.Clear />;
  let className = '';
  let text = '';

  if (callEvent.isDnc) {
    className = 'error';
    text = 'DNC (pressed 9)';
  } else if (callEvent.isTransfer) {
    icon = <icons.NorthEast />;
    text = 'transferred (pressed 1)';
  } else if (callEvent.noResponse) {
    className = 'error';
    text = 'no response';
  }

  return (
    <>
      <CallStatusIcon className={className}>{icon}</CallStatusIcon>
      <ChatBubble className="call">
        Inbound call <>&mdash;</> {text}
      </ChatBubble>
    </>
  );
};

export const Recording: FC<{
  recording: Conversation_externalLead_conversation_recording;
}> = ({ recording }) => {
  const expires = extractExpiration(recording.recordingUrl);
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    if (expires == null) {
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <ChatBubble className="call" sx={{ width: '100%' }}>
      <Stack>
        <Typography>
          {recording.recordingUrl ? 'Recording' : 'Recording not available'} <>&mdash;</>{' '}
          {recording.isTransfer ? 'qualified transfer' : 'non-qualified transfer'}
        </Typography>
        {recording.recordingUrl ? (
          <audio controls src={recording.recordingUrl} style={{ width: '100%', minWidth: 300 }} />
        ) : (
          <Typography>Duration: {formatDuration(recording.duration)}</Typography>
        )}

        {expires != null && expires < currentTime && (
          <Typography color="#ff3f14">
            For security purposes, access to this recording has expired. To play the recording,
            please refresh the page.
          </Typography>
        )}
      </Stack>
    </ChatBubble>
  );
};

export const LeadEventCard: FC<{
  leadEvent: Conversation_externalLead_conversation_leadEvent;
  leadTimeZone: string;
}> = ({ leadEvent, leadTimeZone }) => {
  const firstAppointmentDetails = leadEvent.events?.[0]?.appointmentDetails;

  if (!firstAppointmentDetails) {
    return null;
  }

  let displayStatus = firstAppointmentDetails.appointmentStatus?.toLowerCase() || '';
  let statusColor = '#CBCED2';
  let statusIcon = <icons.Event />;

  if (leadEvent.cancelledTime) {
    displayStatus = 'cancelled';
  }

  switch (displayStatus) {
    case 'confirmed':
      statusColor = '#91E0C4';
      statusIcon = <icons.EventAvailable />;
      break;
    case 'cancelled':
      statusColor = '#E99987';
      statusIcon = <icons.EventBusy />;
      break;
    case 'rescheduled':
      statusColor = '#F3DA99';
      statusIcon = <icons.Update />;
      break;
    default:
      statusColor = '#c7b5f3';
      break;
  }

  const appointmentTime = leadEvent.rescheduledTime ?? leadEvent.eventTime;

  const timeChanges: {
    time: string;
    type: 'original' | 'rescheduled_to';
  }[] = [];

  leadEvent.events?.forEach((event) => {
    if (
      !event?.appointmentDetails?.previousAppointmentStartTime &&
      event?.appointmentDetails?.appointmentStartTime
    ) {
      timeChanges.push({
        time: event.appointmentDetails.appointmentStartTime,
        type: 'original',
      });
    }
    if (
      event?.appointmentDetails?.previousAppointmentStartTime &&
      event?.appointmentDetails?.appointmentStartTime &&
      new Date(event.appointmentDetails.previousAppointmentStartTime).getTime() !=
        new Date(event.appointmentDetails.appointmentStartTime).getTime()
    ) {
      timeChanges.push({
        time: event.appointmentDetails.appointmentStartTime,
        type: 'rescheduled_to',
      });
    }
  });

  timeChanges.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const formattedTime = appointmentTime
    ? moment(appointmentTime).tz(leadTimeZone).format('dddd MMM D, YYYY [at] h:mm A z')
    : 'Time not specified';

  const StatusIconBox = styled(Box)(() => ({
    borderRadius: '50%',
    height: 30,
    width: 30,
    backgroundColor: statusColor,
    color: 'white',
    textAlign: 'center',
    fontSize: 15,
    padding: '2px 0px',
  }));

  return (
    <ChatBubble className="call" sx={{ width: '100%' }}>
      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <StatusIconBox>{statusIcon}</StatusIconBox>
          <Typography fontWeight="medium">
            {firstAppointmentDetails.title || 'Appointment'}
          </Typography>
          {displayStatus && <LeadStatusChip status={displayStatus} />}
        </Box>

        <Stack spacing={0.5}>
          <Typography>
            <icons.Schedule fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            {formattedTime}
          </Typography>
          {firstAppointmentDetails.contactId && firstAppointmentDetails.locationId && (
            <Typography>
              <icons.AccountBox fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              <a
                href={`https://app.gohighlevel.com/v2/location/${firstAppointmentDetails.locationId}/contacts/detail/${firstAppointmentDetails.contactId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#318adc', textDecoration: 'none' }}
              >
                Contact Details{' '}
                <icons.OpenInNewOutlined
                  color="action"
                  fontSize="small"
                  sx={{ verticalAlign: 'middle' }}
                />
              </a>
            </Typography>
          )}

          {firstAppointmentDetails.contactId && !firstAppointmentDetails.locationId && (
            <Typography>
              <icons.AccountBox fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Contact ID: {firstAppointmentDetails.contactId}
            </Typography>
          )}

          {/*{firstAppointmentDetails.address && (*/}
          {/*  <Typography>*/}
          {/*    <icons.LocationOn fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />*/}
          {/*    {mapsLink ? (*/}
          {/*      <a*/}
          {/*        href={mapsLink}*/}
          {/*        target="_blank"*/}
          {/*        rel="noopener noreferrer"*/}
          {/*        style={{ color: '#318adc', textDecoration: 'none' }}*/}
          {/*      >*/}
          {/*        {firstAppointmentDetails.address}*/}
          {/*      </a>*/}
          {/*    ) : (*/}
          {/*      firstAppointmentDetails.address*/}
          {/*    )}*/}
          {/*  </Typography>*/}
          {/*)}*/}

          {timeChanges.length > 1 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 0.5 }}>
                Appointment Timeline:
              </Typography>

              {timeChanges.map((change, index) => {
                let icon: ReactNode, label: string, textColor: string;

                switch (change.type) {
                  case 'original':
                    icon = <icons.Flag fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />;
                    label = 'Initially scheduled for';
                    textColor = 'text.secondary';
                    break;
                  case 'rescheduled_to':
                    icon = (
                      <icons.Update fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    );
                    label = 'Changed to';
                    textColor = '#684b00';
                    break;
                  default:
                    icon = <icons.Event fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />;
                    label = 'Scheduled for';
                    textColor = 'text.primary';
                    break;
                }

                return (
                  <Typography key={index} variant="body2" sx={{ color: textColor, ml: 1 }}>
                    {icon}
                    {label}:{' '}
                    {moment(change.time).tz(leadTimeZone).format('MMM D, YYYY [at] h:mm A z')}
                  </Typography>
                );
              })}
            </Box>
          )}

          {leadEvent.cancelledTime && (
            <Typography sx={{ mt: 0.5, color: '#E99987', fontWeight: 'medium' }}>
              <icons.Cancel fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Cancelled at {moment(leadEvent.cancelledTime).format('MMM D, YYYY [at] h:mm A')}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ChatBubble>
  );
};

export const LinkClickEventCard: FC<{
  linkClickedDetails: Conversation_externalLead_conversation_leadEvent_events_linkClickedDetails;
  leadTimeZone: string;
}> = ({ linkClickedDetails, leadTimeZone }) => {
  const suspiciousBotClick = linkClickedDetails.ipClickedCount > 1;

  const formattedTime = linkClickedDetails.clickedAt
    ? moment(linkClickedDetails.clickedAt).tz(leadTimeZone).format('h:mm A z')
    : undefined;

  const cardColor = suspiciousBotClick ? '#fff3eb' : '#e8f5e9';
  const iconColor = suspiciousBotClick ? '#ff8000' : '#388e3c';

  const StatusIconBox = styled(Box)(() => ({
    borderRadius: '50%',
    height: 24,
    width: 24,
    backgroundColor: iconColor,
    color: 'white',
    textAlign: 'center',
    fontSize: 13,
    padding: '2px 0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }));

  return (
    <Box
      sx={{
        paddingLeft: 30,
        backgroundColor: cardColor,
        borderRadius: 1,
        marginLeft: 1,
        padding: '12px',
        width: '100%',
        fontWeight: 500,
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <StatusIconBox>
          <icons.Link sx={{ fontSize: 14 }} />
        </StatusIconBox>
        <Typography variant="body2" fontWeight="medium">
          Link Clicked{' '}
          {formattedTime && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: '4px' }}
            >
              at {formattedTime}
            </Typography>
          )}
        </Typography>
        {linkClickedDetails.ip && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 'auto', textAlign: 'right' }}
          >
            IP: {linkClickedDetails.ip}
            <br />
            {suspiciousBotClick &&
              `This IP may be a bot due to clicking ${
                linkClickedDetails.ipClickedCount - 1
              } other link${linkClickedDetails.ipClickedCount > 2 ? 's' : ''}`}
          </Typography>
        )}
      </Box>
      {/*{linkClickedDetails.userAgent && (*/}
      {/*  <Typography>*/}
      {/*    <icons.Devices fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />*/}
      {/*    {linkClickedDetails.userAgent}*/}
      {/*  </Typography>*/}
      {/*)}*/}
    </Box>
  );
};

const SystemMediaMessage: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography component="span" fontStyle="italic">
    {children}
  </Typography>
);

export const Media: FC<{ media: Conversation_externalLead_conversation_inboundSms_media }> = ({
  media,
}) => {
  if (!media.url) {
    return (
      <Box>
        <SystemMediaMessage>
          Media with type &quot;{media.contentType}&quot; is not available
        </SystemMediaMessage>
      </Box>
    );
  }

  if (media.contentType?.startsWith('image')) {
    return (
      <Box component="img" src={media.url} borderRadius="5px" minWidth="300px" maxWidth="100%" />
    );
  }
  if (media.contentType?.startsWith('audio')) {
    return (
      <audio controls>
        <source src={media.url} type={media.contentType} style={{ width: '100%', minWidth: 300 }} />
      </audio>
    );
  }
  if (media.contentType?.startsWith('video')) {
    return (
      <video controls>
        <source src={media.url} type={media.contentType} style={{ width: '100%', minWidth: 300 }} />
      </video>
    );
  }

  return (
    <Box>
      <SystemMediaMessage>
        File with type &quot;{media.contentType}&quot; cannot be previewed
      </SystemMediaMessage>
      <TooltipIconButtonLink link={media.url} tooltip="Open in new tab">
        <icons.LaunchRounded />
      </TooltipIconButtonLink>
    </Box>
  );
};

export const InboundSmsAndMedia: FC<{
  inboundSms: Conversation_externalLead_conversation_inboundSms;
}> = ({ inboundSms }) => {
  return (
    <ChatBubble>
      <Stack gap={1}>
        {inboundSms.media?.map((m) => (
          <Media key={m.url} media={m} />
        ))}
        {inboundSms.body}
      </Stack>
    </ChatBubble>
  );
};

export const ScheduledPreviewBubble: FC<{
  item: Conversation_externalLead_scheduledOutreach;
  tz: string;
}> = ({ item, tz }) => {
  const when = moment(item.when).tz(tz);
  const previewRaw = item.messagePreview?.trim();
  const preview = previewRaw?.replace(/\\n/g, '\n');

  const header = (
    <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
      <Box component="span" sx={{ verticalAlign: 'middle', display: 'inline-flex', mr: 1 }}>
        {iconForItem(item)}
      </Box>
      {labelForItem(item)} {when.fromNow()}
    </Typography>
  );

  return (
    <ChatMessage className="right">
      <Box display="flex" alignItems="center">
        <ChatBubble
          className="right"
          sx={{
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            maxWidth: 680,
            paddingRight: 2,
          }}
        >
          {header}
          {preview && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{preview}</Typography>}
        </ChatBubble>
        <AktifyIcon2 />
      </Box>
      <Typography variant="body2" color="textSecondary" paddingX="38px">
        <Timestamp>{when.format('MM/D/YYYY h:mm A')}</Timestamp>
      </Typography>
    </ChatMessage>
  );
};
